import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { BiQrScan, BiSolidEdit } from "react-icons/bi"
import { GoTasklist } from "react-icons/go"
import { IoArrowForwardOutline, IoClose, IoCloseOutline } from "react-icons/io5"
import { LuMessageCircleQuestion } from "react-icons/lu"
import { MdOutlineArrowForwardIos } from "react-icons/md"
import { useNavigate } from "react-router-dom"

import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import { EventDocData, TEventDetail } from "@letshang/constants"
import { ROUTES } from "src/App"

import { functionsAccessor } from "@firebaseConfig/clientApp"

import {
  TDeviceType,
  useDeviceTypeByWidth,
} from "@fuse/customHooks/useDeviceTypeByWidth"

import DownloadAppModalV4, {
  DownloadAppModalHandle,
} from "./DownloadAppModalV4"

export interface SettingsBottomSheetHandle {
  openModal: () => void
  closeModal: () => void
}

const SettingsBottomSheet = forwardRef<
  SettingsBottomSheetHandle,
  {
    eventId: string
    eventDocData: EventDocData
    allowEdit?: boolean
    eventDetails: TEventDetail
    onGuestList: () => void
    onCancelEvent?: () => void
  }
>(
  (
    {
      eventId,
      eventDocData,
      onGuestList,
      allowEdit,
      eventDetails,
      onCancelEvent,
    },
    ref,
  ) => {
    const [isVisible, setIsVisible] = useState(false)
    const [showReachOut, setShowReachOut] = useState(false)
    const [reachOutContext, setReachOutContext] = useState<{
      title: string
      description: string
      message: string
      feature: string
    }>({ title: "", description: "", message: "", feature: "" })
    const [isSending, setIsSending] = useState(false)
    const navigate = useNavigate()
    const modalRef = useRef<DownloadAppModalHandle>(null)
    const deviceType = useDeviceTypeByWidth()
    const isMobileDevice = deviceType === TDeviceType.MOBILE

    const onOpenDownloadAppModal = () => {
      modalRef.current?.openModal()
    }

    const handleReachOut = (
      feature: string,
      title: string,
      description: string,
      message: string,
    ) => {
      setReachOutContext({ title, description, message, feature })
      setShowReachOut(true)
      setIsVisible(false) // Close settings modal
    }

    const handleCloseReachOut = () => {
      setShowReachOut(false)
    }

    const handleSendReachOut = async () => {
      if (!reachOutContext.message?.trim()) {
        showErrorToast("Please enter a message first.")
        return
      }

      setIsSending(true)
      try {
        const sendFeedback = functionsAccessor.httpsCallable(
          "eventManagement-sendFeedbackForEvent",
        )
        const response = await sendFeedback({
          eventId: eventId || "web-assistance-request",
          feedback: `[${reachOutContext.feature} Assistance Request] ${reachOutContext.message}`,
        })

        const result = response.data as { success: boolean; message: string }
        if (result.success) {
          setShowReachOut(false)
          showSuccessToast("Message sent!", {
            text2: "Our team will reach out to you.",
          })
        } else {
          showErrorToast(
            result.message || "Failed to send message. Please try again.",
          )
        }
      } catch (error) {
        console.error("Error sending reach out message:", error)
        showErrorToast("Failed to send message. Please try again.")
      } finally {
        setIsSending(false)
      }
    }

    const optionsList = [
      {
        title: "Edit",
        icon: <BiSolidEdit size={20} />,
        isDisabled: false,
        onPress: () => {
          if (allowEdit) {
            navigate(ROUTES.EDIT_EVENT.replace(":eventId", eventId), {
              state: {
                eventDocData: eventDocData,
                eventDetails,
              },
            })
          } else {
            showErrorToast("Event is closed or cancelled.")
          }
        },
      },
      {
        title: "Guest List",
        icon: <GoTasklist size={20} />,
        isDisabled: false,
        onPress: () => {
          onGuestList()
          setIsVisible(false)
        },
      },
      {
        title: "Add Questionnaires",
        icon: <LuMessageCircleQuestion size={20} />,
        isDisabled: true,
        isAdvanced: true,
        onPress: () => {
          handleReachOut(
            "questionnaires",
            "Need Help with Questionnaires?",
            "Our team will help you set up questionnaires to collect attendee information.",
            "Hi, I'd like assistance with adding questionnaires to collect information from my event attendees. Could you help me set this up?",
          )
        },
      },
      {
        title: "Text Blasts",
        icon: <BiQrScan size={20} />,
        isDisabled: true,
        isAdvanced: true,
        onPress: () => {
          handleReachOut(
            "text-blasts",
            "Need Help with Text Blasts?",
            "Our team will help you set up automated text messaging for your event.",
            "Hi, I'd like assistance with sending text message updates to my event attendees. Could you help me set up text blasts?",
          )
        },
      },
      {
        title: "Scan Tickets",
        icon: <BiSolidEdit size={20} />,
        isDisabled: true,
        isAdvanced: true,
        onPress: () => {
          handleReachOut(
            "ticket-scanning",
            "Need Help with Ticket Scanning?",
            "Our team will help you set up ticket validation for your event.",
            "Hi, I'd like assistance with scanning and validating tickets at my event entrance. Could you help me set up ticket scanning?",
          )
        },
      },
    ]

    const cancelOption = {
      title: "Cancel this event",
      icon: <IoClose size={20} />,
      isDisabled: false,
      isDestructive: true,
      onPress: () => {
        onCancelEvent && onCancelEvent()
        setIsVisible(false)
      },
    }

    // Get the registration details for the description field

    useImperativeHandle(ref, () => ({
      openModal: () => {
        setIsVisible(true)
      },
      closeModal: () => {
        setIsVisible(false)
      },
    }))

    return (
      <>
        {isVisible && (
          <div
            className={` ${"fixed inset-0 flex justify-center bg-black bg-opacity-50 z-[100] items-end md:items-center place-items-center"} `}
            onClick={(e) => {
              // Only close if clicking the backdrop (the outer div itself)
              if (e.target === e.currentTarget) {
                setIsVisible(false)
              }
            }}
          >
            <BlurWrapper
              overlayOpacityDefault="60"
              blurIntensityDefault="lg"
              className="w-full md:max-w-sm !bg-gray-500/10 rounded-t-2xl md:rounded-2xl p-6 relative flex flex-col cursor-auto place-content-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative flex flex-col items-center justify-between gap-4">
                {optionsList.map((item, index) => (
                  <div
                    key={index}
                    className="flex w-full items-center justify-center gap-4"
                    onClick={item.onPress}
                  >
                    <div
                      className={` p-2 bg-white/10 rounded-lg ${
                        !item.isDisabled
                          ? "text-white"
                          : (item as any).isAdvanced
                            ? "text-white/25"
                            : "text-white/35"
                      }`}
                    >
                      {item.icon}
                    </div>
                    <div
                      className={`flex-1 text-white text-left ${
                        !item.isDisabled
                          ? "text-white"
                          : (item as any).isAdvanced
                            ? "text-white/25"
                            : "text-white/35"
                      }`}
                    >
                      {item.title}
                    </div>
                    <div>
                      <MdOutlineArrowForwardIos
                        className={`${!item.isDisabled ? "text-white" : (item as any).isAdvanced ? "text-white/25" : "text-white/35"}`}
                        size={20}
                      />
                    </div>
                  </div>
                ))}

                {/* Separator line */}
                {onCancelEvent && (
                  <div className="w-full h-px bg-white/10 my-2" />
                )}

                {/* Cancel Event Option */}
                {onCancelEvent && (
                  <div
                    className="flex w-full items-center justify-center gap-4 cursor-pointer"
                    onClick={cancelOption.onPress}
                  >
                    <div className="p-2 bg-red-500/20 rounded-lg text-red-400">
                      {cancelOption.icon}
                    </div>
                    <div className="flex-1 text-red-400 text-left">
                      {cancelOption.title}
                    </div>
                    <div>
                      <MdOutlineArrowForwardIos
                        className="text-red-400"
                        size={20}
                      />
                    </div>
                  </div>
                )}
              </div>
            </BlurWrapper>
            <DownloadAppModalV4 ref={modalRef} showOnTop={!isMobileDevice} />
          </div>
        )}

        {showReachOut && (
          <div
            className="fixed inset-0 z-[110] flex items-center justify-center"
            onClick={handleCloseReachOut}
          >
            <div className="fixed inset-0 bg-black/40" />
            <div
              className="relative w-full max-w-md mx-4 bg-[#252525] rounded-2xl p-6 z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-white text-xl font-medium">
                  {reachOutContext.title}
                </h2>
                <button
                  onClick={handleCloseReachOut}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <IoCloseOutline size={24} />
                </button>
              </div>
              <div className="space-y-4">
                <textarea
                  value={reachOutContext.message}
                  onChange={(e) =>
                    setReachOutContext((prev) => ({
                      ...prev,
                      message: e.target.value,
                    }))
                  }
                  placeholder="Type your message..."
                  className="w-full min-h-[120px] bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-white/50 focus:outline-none focus:border-white/40 resize-none"
                />
                <button
                  onClick={handleSendReachOut}
                  disabled={isSending}
                  className={`w-full py-3 px-4 bg-white/20 hover:bg-white/30 border border-white/20 rounded-lg text-white font-medium transition-colors ${
                    isSending ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isSending ? "Sending..." : "Send message"}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    )
  },
)

export default SettingsBottomSheet
